import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/NumericField';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ValidationSummaryPanel } from '@/components/ui/ValidationSummaryPanel';
import { SectionProgressIndicator, createReversePitiSections } from '@/components/ui/SectionProgressIndicator';
import { 
  ArrowLeft, 
  DollarSign, 
  Home, 
  TrendingUp, 
  Shield, 
  Info,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { useCalculatorValidation } from '@/hooks/useCalculatorValidation';
import { LoanTypeOption } from '@/components/calculator/utils/types';
import { useReversePitiState, VAStatus, VADisabilityStatus } from '@/hooks/calculator/useReversePitiState';
import { RateSourceIndicator } from '@/components/calculator/RateSourceIndicator';
import { CalculatorResetButton } from '@/components/calculator/CalculatorResetButton';
import { useCalculatorReset } from '@/hooks/calculator/useCalculatorReset';
import { useCalculatorExport } from '@/hooks/calculator/useCalculatorExport';
import { StateExportImport } from '@/components/calculator/StateExportImport';

import MobileReversePitiPieChart from './MobileReversePitiPieChart';

type ActiveView = 'inputs' | 'results';

export const MobileReversePitiView = ({ calculator }: { calculator: ReturnType<typeof useReversePitiState> }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<ActiveView>('inputs');

  const {
    loanType,
    vaStatus,
    vaDisabilityStatus,
    vaDisabilityRatingPercentage,
    desiredPiti,
    formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi,
    setDesiredPiti,
    setInterestRate,
    setLoanTerm,
    setDownPaymentPercent,
    setPropertyTaxRate,
    setAnnualInsurance,
    setHoaFees,
    setPmiRate,
    setIncludePmi,
    handleLoanTypeChange,
    handleVAStatusChange,
    handleVADisabilityStatusChange,
    handleVADisabilityPercentageChange,
    isUsingLiveRate,
    results,
    formatCurrency,
    resetToDefaults,
    getExportableState,
    loadFromExport,
  } = calculator;

  // Calculator reset hook
  const { resetCalculator, isResetting } = useCalculatorReset({
    formSlug: 'reverse_piti_mobile',
    onReset: resetToDefaults,
  });

  // Calculator export/import hook
  const { exportState, importState } = useCalculatorExport({
    calculatorType: 'reverse-piti',
    getState: getExportableState,
    loadState: loadFromExport,
  });

  // Use the shared haptic hook
  const { triggerHaptic } = useHapticFeedback();

  // Sound effects for accessibility
  const { playSound } = useSoundEffects();

  // Calculator validation
  const validation = useCalculatorValidation({
    desiredPiti,
    interestRate: formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    loanType,
    includePmi,
  });

  // Section progress indicator data
  const sectionStatuses = createReversePitiSections({
    desiredPiti,
    interestRate: formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi,
  });

  // Field refs for scroll-to-error
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const scrollToField = (field: string) => {
    const el = fieldRefs.current[field];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    validation.setShowSummary(false);
  };

  // Handler to validate and navigate to results
  const handleViewResults = () => {
    if (validation.validate()) {
      setActiveView('results');
      triggerHaptic('success');
      playSound('success');
    } else {
      triggerHaptic('error');
      playSound('error');
    }
  };

  // Swipe gesture handlers - validate before swiping to results
  const swipeHandlers = useSwipeGesture({
    threshold: 50,
    onSwipeLeft: () => {
      if (activeView === 'inputs') {
        handleViewResults();
      }
    },
    onSwipeRight: () => {
      if (activeView === 'results') {
        setActiveView('inputs');
        triggerHaptic('light');
      }
    },
  });

  return (
    <div 
      className="min-h-screen pb-24 pt-safe"
      {...swipeHandlers}
    >
      {/* Header */}
      <div className="relative z-10 bg-background/95 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/calculator-mobile')}
            className="gap-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-base font-semibold text-foreground">Reverse PITI</h1>
          <div className="flex items-center gap-1">
            <StateExportImport
              onExport={exportState}
              onImport={importState}
              variant="icon"
            />
            <CalculatorResetButton
              onReset={resetCalculator}
              isResetting={isResetting}
              variant="icon"
            />
          </div>
        </div>
        
        {/* View indicator dots */}
        <div className="flex justify-center gap-2 mt-2">
          <div 
            className={`w-2 h-2 rounded-full transition-colors ${
              activeView === 'inputs' ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          />
          <div 
            className={`w-2 h-2 rounded-full transition-colors ${
              activeView === 'results' ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Validation Summary Panel */}
        <ValidationSummaryPanel
          errors={validation.errors}
          isVisible={validation.showSummary}
          onDismiss={() => validation.setShowSummary(false)}
          onFieldClick={scrollToField}
          className="mb-4"
        />

        {/* Section Progress Indicator - Compact for mobile */}
        {activeView === 'inputs' && (
          <Card className="border-border/50">
            <CardContent className="p-3">
              <SectionProgressIndicator sections={sectionStatuses} compact />
            </CardContent>
          </Card>
        )}

        {activeView === 'inputs' ? (
          <>
            {/* Primary Input - Desired PITI */}
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-4">
                <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-primary" />
                  Desired Monthly PITI
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">$</span>
                  <NumericField
                    aria-label="Desired Monthly PITI"
                    value={desiredPiti}
                    onChange={setDesiredPiti}
                    className="pl-10 h-14 text-xl font-bold text-center"
                    min={0}
                    step={100}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Accordion Sections */}
            <Accordion type="multiple" defaultValue={['loan-details']} className="space-y-2">
              {/* Loan Details */}
              <AccordionItem value="loan-details" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <span className="text-sm font-medium">Loan Details</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  {/* Loan Type */}
                  <div className="space-y-2">
                    <Label className="text-sm">Loan Type</Label>
                    <Select value={loanType} onValueChange={(val) => handleLoanTypeChange(val as LoanTypeOption)}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conventional">Conventional</SelectItem>
                        <SelectItem value="fha">FHA</SelectItem>
                        <SelectItem value="va">VA</SelectItem>
                        <SelectItem value="usda">USDA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* VA Options */}
                  {loanType === 'va' && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <Label className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          VA Loan Details
                        </Label>
                      </div>
                      
                      <Select value={vaStatus || ""} onValueChange={(value) => handleVAStatusChange(value as VAStatus)}>
                        <SelectTrigger className="h-12 bg-background">
                          <SelectValue placeholder="Military Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="activeDuty">Active Duty</SelectItem>
                          <SelectItem value="separatedRetired">Separated/Retired</SelectItem>
                        </SelectContent>
                      </Select>

                      {vaStatus === 'separatedRetired' && (
                        <Select value={vaDisabilityStatus || ""} onValueChange={(value) => handleVADisabilityStatusChange(value as VADisabilityStatus)}>
                          <SelectTrigger className="h-12 bg-background">
                            <SelectValue placeholder="Disability Rating" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Rating</SelectItem>
                            <SelectItem value="partial">10% to 90%</SelectItem>
                            <SelectItem value="full">100%</SelectItem>
                          </SelectContent>
                        </Select>
                      )}

                      {vaStatus === 'separatedRetired' && vaDisabilityStatus === 'partial' && (
                        <div className="space-y-2 p-2 bg-muted/50 rounded-lg">
                          <div className="flex justify-between items-center">
                            <Label className="text-xs">Disability Rating</Label>
                            <span className="text-sm font-semibold text-primary">{vaDisabilityRatingPercentage}%</span>
                          </div>
                          <Slider
                            value={[vaDisabilityRatingPercentage]}
                            onValueChange={(values) => handleVADisabilityPercentageChange(values[0])}
                            min={10}
                            max={90}
                            step={10}
                            className="py-2"
                          />
                        </div>
                      )}

                      {(vaDisabilityStatus === 'partial' || vaDisabilityStatus === 'full') && (
                        <div className="flex items-start gap-2 p-2 bg-green-50 dark:bg-green-950/30 rounded-lg">
                          <Info size={14} className="text-green-600 mt-0.5" />
                          <p className="text-xs text-green-700 dark:text-green-300">
                            0% down, funding fee waived
                            {vaDisabilityStatus === 'full' && ', property tax exempt'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Interest Rate & Term */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm">Interest Rate</Label>
                      <div className="relative">
                        <NumericField
                          aria-label="Interest Rate"
                    value={formattedInterestRate}
                          onChange={setInterestRate}
                          className="h-12 pr-8"
                          step={0.125}
                          min={0}
                          max={20}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                      <RateSourceIndicator isLive={isUsingLiveRate} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Loan Term</Label>
                      <Select value={String(loanTerm)} onValueChange={(val) => setLoanTerm(Number(val))}>
                        <SelectTrigger className="h-12">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 years</SelectItem>
                          <SelectItem value="20">20 years</SelectItem>
                          <SelectItem value="15">15 years</SelectItem>
                          <SelectItem value="10">10 years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Down Payment */}
                  <div className="space-y-2">
                    <Label className="text-sm">Down Payment</Label>
                    <div className="relative">
                        <NumericField
                          aria-label="Down Payment Percent"
                    value={downPaymentPercent}
                          onChange={setDownPaymentPercent}
                          className="h-12 pr-8"
                          step={1}
                          min={0}
                          max={100}
                        />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Additional Costs */}
              <AccordionItem value="additional-costs" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <span className="text-sm font-medium">Additional Costs</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  {/* Property Tax Rate */}
                  <div className="space-y-2">
                    <Label className="text-sm">Property Tax Rate</Label>
                    <div className="relative">
                        <NumericField
                          aria-label="Property Tax Rate"
                    value={propertyTaxRate}
                          onChange={setPropertyTaxRate}
                          className="h-12 pr-8"
                          step={0.1}
                          min={0}
                          max={10}
                        />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                    </div>
                  </div>

                  {/* Annual Insurance */}
                  <div className="space-y-2">
                    <Label className="text-sm">Annual Insurance</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <NumericField
                          aria-label="Annual Insurance"
                    value={annualInsurance}
                          onChange={setAnnualInsurance}
                          className="h-12 pl-8 pr-12"
                          step={100}
                          min={0}
                        />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">/yr</span>
                    </div>
                  </div>

                  {/* HOA */}
                  <div className="space-y-2">
                    <Label className="text-sm">HOA Fees (Monthly)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <NumericField
                          aria-label="HOA Fees Monthly"
                    value={hoaFees}
                          onChange={setHoaFees}
                          className="h-12 pl-8"
                          step={25}
                          min={0}
                        />
                    </div>
                  </div>

                  {/* PMI */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Include PMI</Label>
                      <Switch
                        checked={includePmi}
                        onCheckedChange={setIncludePmi}
                        disabled={loanType === 'va' || loanType === 'usda'}
                      />
                    </div>
                    {includePmi && (
                      <div className="relative">
                        <NumericField
                          aria-label="PMI Rate"
                    value={pmiRate}
                          onChange={setPmiRate}
                          className="h-12 pr-8"
                          step={0.1}
                          min={0}
                          max={5}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        ) : (
          <>
            {/* Results View */}
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Home className="w-6 h-6 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Maximum Home Price</span>
                </div>
                <p className="text-4xl font-bold text-primary">
                  {formatCurrency(results.maxHomePrice)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Based on {formatCurrency(desiredPiti)}/month PITI
                </p>
              </CardContent>
            </Card>

            {/* Pie Chart */}
            <Card>
              <CardContent className="p-4">
                <MobileReversePitiPieChart 
                  principalInterest={results.monthlyPrincipalInterest}
                  taxes={results.monthlyPropertyTax}
                  insurance={results.monthlyInsurance}
                  hoa={results.monthlyHoa}
                  pmi={results.monthlyPmi}
                  totalPayment={results.totalPiti}
                />
              </CardContent>
            </Card>

            {/* Loan Summary */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Loan Summary
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Loan Amount</p>
                    <p className="font-semibold">{formatCurrency(results.loanAmount)}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Down Payment</p>
                    <p className="font-semibold">{formatCurrency(results.downPayment)}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Interest Rate</p>
                    <p className="font-semibold">{formattedInterestRate}%</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Loan Term</p>
                    <p className="font-semibold">{loanTerm} years</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="calculator-bottom-actions fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t border-border p-4 pb-safe z-50">
        <div className="flex gap-3">
          {activeView === 'inputs' ? (
            <Button 
              className="flex-1 h-14 text-base gap-2"
              onClick={handleViewResults}
            >
              View Results
              <ChevronRight className="w-5 h-5" />
            </Button>
          ) : (
            <>
              <Button 
                variant="outline"
                className="flex-1 h-14 text-base gap-2"
                onClick={() => {
                  setActiveView('inputs');
                  triggerHaptic('light');
                }}
              >
                <ChevronLeft className="w-5 h-5" />
                Edit Inputs
              </Button>
              <Button 
                className="flex-1 h-14 text-base"
                onClick={() => navigate('/calculator-mobile')}
              >
                Done
              </Button>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

const MobileReversePitiContent = () => {
  const calculator = useReversePitiState();
  return <MobileReversePitiView calculator={calculator} />;
};

export default MobileReversePitiContent;
